import {
  Button,
  ToastNotifications,
  Tooltip,
  Text,
  Icon,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import classNames from 'classnames'
import React from 'react'
import { buildRetailerDetails } from '../../../../functions/invoicing/payment-request-resources'
import { validateMerchantAccount } from '../../../../functions/invoicing/validate-merchant-account'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectOutstandingBalance } from '../../../../state/slices/currentSale/currentSaleSlice'
import { PaymentRequestQueryResponse } from '../../../../functions/invoicing/fetch-invoicing-payment-requests'
import { getMinimumLsPayAmount } from '../../../../util/lspay-utils'
import { useFulfillmentTakeNowSaleData } from '../../useFulfillmentTakeNowSaleData'
import { useFormatCurrencyValue } from '../../summary-panel/useFormatCurrencyValue'
import { usePaymentRecalculationLock } from '../PaymentRecalculationLockContext'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { FEATURE_INTEGRATED_PAYMENT_OVERPAYMENT_FIX } from '../../../../constants'
import { CancelPaymentRequestModalWrapper } from './CancelPaymentRequestModalWrapper'
import { PaymentRequestModalWrapper } from './PaymentRequestModalWrapper'
import { usePaymentRequestCalculations } from './hooks/usePaymentRequestCalculations'
import { useShowPaymentRequestButton } from './hooks/useShowPaymentRequestButton'

interface PaymentRequestButtonProps {
  amountTendered: string // is the amount entered for partial payments on the Payment Panel, if not edited then equals the sale outstanding balance
  paymentRequests: PaymentRequestQueryResponse[]
  onRefetchPaymentRequests: () => Promise<void>
  hasPaymentAmountError: boolean // payment amount validation state passed from PaymentPanel
  fullWidth?: boolean // when false, button renders inline with other payment methods (e.g. when customer has saved cards)
  /** Fail-closed lock (e.g. sale ACH fetch loading / error / leftover $0). */
  disabled?: boolean
}

export const PaymentRequestButton: React.FC<PaymentRequestButtonProps> = ({
  amountTendered,
  paymentRequests,
  onRefetchPaymentRequests,
  hasPaymentAmountError,
  fullWidth = true,
  disabled = false,
}) => {
  const [showPaymentRequestModal, setShowPaymentRequestModal] = React.useState(
    false
  )
  const [showCancelModal, setShowCancelModal] = React.useState(false)
  const [isValidatingMerchant, setIsValidatingMerchant] = React.useState(false)
  const t = useT()
  const { arePaymentActionsLocked } = usePaymentRecalculationLock()
  const overpaymentFixEnabled = getFeatureFromCache(
    FEATURE_INTEGRATED_PAYMENT_OVERPAYMENT_FIX
  )
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const registerManager = useRegisterManager()
  const retailer = useRetailerSettingsManager().get()
  const currentOutlet = registerManager.getActiveOutlet()
  const {
    takeNowItemsBalance,
    shouldShowComplexSaleBalanceBreakdown: disablePaymentRequestForFulfillmentWithTakeNow,
  } = useFulfillmentTakeNowSaleData()

  const formatCurrencyValue = useFormatCurrencyValue()

  // Calculate pending request data to determine which modal [Cancel or PaymentRequest] and content to show
  const {
    pendingRequestInfo,
    overpaymentThresholdDollars,
  } = usePaymentRequestCalculations(paymentRequests, outstandingBalance)

  // Get currency-specific minimum amount from active outlet
  const { minimumAmount, currencySymbol } = getMinimumLsPayAmount(currentOutlet)

  // Check payment amount is valid and meets the minimum threshold for LSPAY and Payment Requests
  const paymentAmount = hasPaymentAmountError ? 0 : Number(amountTendered)
  const isAmountBelowMinimum =
    !hasPaymentAmountError && paymentAmount < minimumAmount

  const { showButton, canLayaway } = useShowPaymentRequestButton({
    amountTendered,
    hasPaymentAmountError,
  })

  if (!showButton) {
    return null
  }

  const retailerDetails = buildRetailerDetails({ retailer })

  const showMerchantValidationError = () => {
    ToastNotifications.negative(
      t(
        'Unable to process payment requests at the moment, merchant validation failed.',
        {
          _context: 'Payment request merchant validation error',
        }
      )
    )
  }

  const tooltipMessage = (
    <T
      _str="Payment amount must be at least {minAmount}"
      _context="tooltip message for payment request minimum amount"
      minAmount={`${currencySymbol}${minimumAmount.toFixed(2)}`}
    />
  )

  const tooltipHeader = t('Minimum payment amount required', {
    _context: 'tooltip header for payment request minimum amount',
  })

  const handleButtonClick = async () => {
    if (disablePaymentRequestForFulfillmentWithTakeNow) {
      return
    }
    setIsValidatingMerchant(true)
    try {
      await validateMerchantAccount(retailerDetails)
      const isOverpaying =
        parseFloat(amountTendered) > overpaymentThresholdDollars
      // Show cancel modal if there are pendingRequests and the amountTendered exceeds the overpayment threshold
      if (pendingRequestInfo.requests.length > 0 && isOverpaying) {
        setShowCancelModal(true)
      } else {
        setShowPaymentRequestModal(true)
      }
    } catch (error) {
      showMerchantValidationError()
    } finally {
      setIsValidatingMerchant(false)
    }
  }

  const buttonContent = (
    <>
      <Tooltip
        className="wr-tooltip"
        direction="center"
        message={tooltipMessage}
        header={tooltipHeader}
        canBeShown={isAmountBelowMinimum}
        icon="info-circle"
      >
        <Button
          jumbo
          className={classNames({
            'btn--payment-request': fullWidth,
            'btn--payment-type btn--payment-request-inline': !fullWidth,
          })}
          variant="supplementary"
          onClick={handleButtonClick}
          disabled={
            disabled ||
            isValidatingMerchant ||
            hasPaymentAmountError ||
            isAmountBelowMinimum ||
            disablePaymentRequestForFulfillmentWithTakeNow ||
            (overpaymentFixEnabled && arePaymentActionsLocked)
          }
        >
          {isValidatingMerchant ? (
            <T
              _str="Preparing payment request form..."
              _context="Payment request button label"
            />
          ) : (
            <T _str="Request payment" _context="Payment request button label" />
          )}
        </Button>
      </Tooltip>
      {disablePaymentRequestForFulfillmentWithTakeNow ? (
        <div
          data-testid="payment-request-disabled-message"
          className="payment-request-disabled-message-container vd-mt2"
        >
          <Text type="supplementary" color="supplementary">
            <Icon icon="fa-info-circle" className="vd-mr1" />
            <T
              _str="Pay {amount} for the take now items before requesting payment"
              _context="Message to inform merchants that they need to pay for take now items before they can request remote payment"
              amount={formatCurrencyValue(takeNowItemsBalance)}
            />
          </Text>
        </div>
      ) : null}
    </>
  )

  return (
    <>
      {fullWidth ? (
        <div className="vd-mt4">{buttonContent}</div>
      ) : (
        buttonContent
      )}
      <PaymentRequestModalWrapper
        showPaymentRequestModal={showPaymentRequestModal}
        setShowPaymentRequestModal={() => setShowPaymentRequestModal(false)}
        amountTendered={amountTendered}
        canLayaway={canLayaway}
      />
      <CancelPaymentRequestModalWrapper
        showCancelModal={showCancelModal}
        setShowCancelModal={() => setShowCancelModal(false)}
        numberOfPaymentRequests={pendingRequestInfo.requests.length}
        totalPaymentRequests={pendingRequestInfo.amountDollars}
        totalToPay={outstandingBalance}
        onRefetchPaymentRequests={onRefetchPaymentRequests}
      />
    </>
  )
}
